import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import type { PortableTextBlock } from 'sanity';
import { cn } from '@/lib/utils';
import { GeneralRulesV2Item } from '@/sanity/pages/PageGeneralRulesV2Template';
import { RuleIcon } from './RuleIcons';
import ExamplesAccordion from './ExamplesAccordion';

const GREEN = '#228e71';
const RED = '#ac0003';

// ── Sub-parts ────────────────────────────────────────────────────────────────

function IconBadge({
  iconType,
  tone,
}: {
  iconType?: string;
  tone: 'default' | 'red' | 'green';
}) {
  if (!iconType) return null;

  const innerBg =
    tone === 'red'
      ? 'bg-[rgba(255,0,4,0.06)]'
      : tone === 'green'
        ? 'bg-[rgba(26,168,127,0.08)]'
        : 'bg-[rgba(0,27,20,0.05)]';

  const iconColor =
    tone === 'red'
      ? 'text-[#ac0003]'
      : tone === 'green'
        ? 'text-[#228e71]'
        : 'text-black';

  return (
    <div className="relative shrink-0 w-[7.25rem] h-[7.25rem]">
      <div className="absolute inset-0 rounded-[0.25rem] border border-[rgba(0,27,20,0.1)]" />
      <div
        className={cn(
          'absolute top-[0.75rem] left-[0.75rem] w-[5.75rem] h-[5.75rem] rounded-[0.25rem]',
          innerBg,
        )}
      />
      <div
        className={cn(
          'absolute top-[2.125rem] left-[2.1875rem] w-[3rem] h-[3rem]',
          iconColor,
        )}
      >
        <RuleIcon type={iconType} />
      </div>
    </div>
  );
}

function BadgePill({ label, tone }: { label: string; tone?: string }) {
  const t = (tone ?? 'neutral').toLowerCase();

  const styles =
    t === 'red'
      ? 'bg-[rgba(255,0,4,0.06)] text-[#ac0003]'
      : t === 'redoutline'
        ? 'border-[0.07rem] lg:border-[0.1rem] border-[#ac0003] border-solid text-[#ac0003]'
        : t === 'green'
          ? 'bg-[rgba(26,168,127,0.08)] text-[#228e71]'
          : 'bg-black/[0.06] text-black';

  return (
    <span
      className={cn(
        'shrink-0 inline-flex items-center leading-[0.9] font-geist-mono font-normal uppercase tracking-[-0.02rem]',
        'rounded-[0.175rem] px-[1.225rem] py-[0.875rem] text-[1.4rem] lg:rounded-[0.25rem] lg:px-[1.75rem] lg:py-[0.75rem] lg:text-[2rem]',
        styles,
      )}
    >
      {label}
    </span>
  );
}

function TopRightMetric({ value, unit }: { value?: string; unit?: string }) {
  if (!value && !unit) return null;
  return (
    <div className="shrink-0 flex flex-col items-end gap-[1.25rem]">
      {value && (
        <p
          className="mb-0 font-geist font-medium text-[2.5rem] leading-[1.3] tracking-[-0.075rem]"
          style={{ color: GREEN }}
        >
          {value}
        </p>
      )}
      {unit && (
        <p className="mb-0 font-geist font-normal text-[2rem] leading-[1.33] tracking-[-0.06rem] uppercase text-black/40">
          {unit}
        </p>
      )}
    </div>
  );
}

function PhaseIndicator({ label, tone }: { label: string; tone?: string }) {
  const green = (tone ?? 'green').toLowerCase() === 'green';
  return (
    <div className="flex items-center gap-[0.75rem]">
      <span
        className={cn(
          'shrink-0 w-[0.75rem] h-[0.75rem] rounded-[0.25rem]',
          green ? '' : 'bg-black/40',
        )}
        style={green ? { backgroundColor: GREEN } : undefined}
      />
      <p className="mb-0 font-geist font-normal text-[1.5rem] leading-[1.33] tracking-[-0.045rem] uppercase text-black/64">
        {label}
      </p>
    </div>
  );
}

function LabeledRichtext({
  label,
  content,
}: {
  label: string;
  content: PortableTextBlock[];
}) {
  return (
    <div className="flex flex-col gap-[0.75rem]">
      <p className="mb-0 font-geist font-normal text-[2rem] leading-[1.33] tracking-[-0.06rem] uppercase text-black/40">
        {label}
      </p>
      <div className="richtext richtext-start text-black/64 [&_p]:mb-0 [&_ul]:mb-0 [&_ol]:mb-0 [&_p]:text-[2.25rem] [&_p]:leading-[1.5] [&_p]:tracking-[-0.0675rem] [&_li]:text-[2.25rem] [&_li]:leading-[1.5] [&_li]:tracking-[-0.0675rem] [&_strong]:text-black [&_strong]:font-medium [&_a]:underline flex flex-col gap-[1rem]">
        <PortableText value={content} components={richTextComponent} />
      </div>
    </div>
  );
}

function PlainRichtext({ content }: { content: PortableTextBlock[] }) {
  return (
    <div className="richtext richtext-start text-black/64 [&_p]:mb-0 [&_ul]:mb-0 [&_ol]:mb-0 [&_p]:text-[2.25rem] [&_p]:leading-[1.5] [&_p]:tracking-[-0.0675rem] [&_li]:text-[2.25rem] [&_li]:leading-[1.5] [&_li]:tracking-[-0.0675rem] [&_strong]:text-black [&_strong]:font-medium [&_a]:underline flex flex-col gap-[1rem]">
      <PortableText value={content} components={richTextComponent} />
    </div>
  );
}

function CardImage({
  image,
  imageMobile,
}: {
  image?: GeneralRulesV2Item['image'];
  imageMobile?: GeneralRulesV2Item['imageMobile'];
}) {
  const hasDesktop = !!image?.src;
  const hasMobile = !!imageMobile?.src;
  if (!hasDesktop && !hasMobile) return null;

  const mobileSrc = hasMobile ? imageMobile : image;
  const desktopSrc = hasDesktop ? image : imageMobile;

  return (
    <>
      <GroqImage
        src={mobileSrc!}
        alt={mobileSrc?.name ?? ''}
        className="!w-full !h-auto lg:!hidden"
      />
      <GroqImage
        src={desktopSrc!}
        alt={desktopSrc?.name ?? ''}
        className="!w-full !h-auto !hidden lg:!block"
      />
    </>
  );
}

function CardCallout({
  label,
  content,
}: {
  label?: string;
  content: PortableTextBlock[];
}) {
  return (
    <div
      className="w-full bg-black/[0.06] border-l-2 px-[2.5rem] py-[1.25rem]"
      style={{ borderColor: GREEN }}
    >
      <div className="richtext richtext-start text-black/64 [&_p]:mb-0 [&_ul]:mb-0 [&_ol]:mb-0 [&_p]:text-[2rem] [&_p]:leading-[1.33] [&_p]:tracking-[-0.06rem] [&_li]:text-[2rem] [&_li]:leading-[1.33] [&_li]:tracking-[-0.06rem] [&_strong]:text-black [&_strong]:font-medium flex flex-col gap-[0.75rem]">
        {label && (
          <p className="mb-0 font-geist font-medium text-[2rem] leading-[1.33] tracking-[-0.06rem] text-black">
            {label}:
          </p>
        )}
        <PortableText value={content} components={richTextComponent} />
      </div>
    </div>
  );
}

// ── Main card ────────────────────────────────────────────────────────────────

export default function RuleCard({ item }: { item: GeneralRulesV2Item }) {
  const border = (item.cardBorderTone ?? 'default').toLowerCase();

  const borderClass =
    border === 'red'
      ? 'border-[#ac0003]/40'
      : border === 'green'
        ? ''
        : 'border-[#2f2f2f]/10';

  const iconTone: 'default' | 'red' | 'green' =
    border === 'red' ? 'red' : border === 'green' ? 'green' : 'default';

  const hasMetric = !!item.metricValue || !!item.metricUnit;
  const hasBadge = !!item.badgeLabel;

  const hasBody = !!item.body?.length;
  const hasWhy = !!item.why?.length;
  const hasHow = !!item.how?.length;
  const hasImage = !!item.image?.src || !!item.imageMobile?.src;
  const hasCallout = !!item.calloutContent?.length;
  const hasExamples = !!item.examplesContent?.length;

  return (
    <div
      className={cn(
        'w-full flex flex-col gap-[3.75rem] p-[1.75rem] lg:p-[2.5rem] rounded-[0.125rem] bg-white border border-solid',
        borderClass,
      )}
      style={border === 'green' ? { borderColor: GREEN } : undefined}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-start lg:items-center justify-between gap-[1.25rem] lg:gap-[2rem]">
        <div className="flex items-center gap-[1.25rem] lg:gap-[2.5rem] min-w-0 flex-1">
          <IconBadge iconType={item.iconType} tone={iconTone} />
          {item.title && (
            <p className="mb-0 font-geist font-medium text-[2.5rem] leading-[1.3] tracking-[-0.075rem] text-[#001b14] min-w-0">
              {item.title}
            </p>
          )}
        </div>

        {hasBadge && (
          <BadgePill label={item.badgeLabel!} tone={item.badgeTone} />
        )}
        {!hasBadge && hasMetric && (
          <TopRightMetric value={item.metricValue} unit={item.metricUnit} />
        )}
      </div>

      {/* ── Phase indicator ─────────────────────────────────────────── */}
      {item.phaseIndicator && (
        <PhaseIndicator
          label={item.phaseIndicator}
          tone={item.phaseIndicatorTone}
        />
      )}

      {/* ── Body ────────────────────────────────────────────────────── */}
      {hasBody && <PlainRichtext content={item.body!} />}

      {/* ── Why ─────────────────────────────────────────────────────── */}
      {hasWhy && (
        <LabeledRichtext label={item.whyLabel || 'WHY?'} content={item.why!} />
      )}

      {/* ── How ─────────────────────────────────────────────────────── */}
      {hasHow && (
        <LabeledRichtext label={item.howLabel || 'HOW?'} content={item.how!} />
      )}

      {/* ── Callout BEFORE image (opt-in) ───────────────────────────── */}
      {hasCallout && item.calloutBeforeImage && (
        <CardCallout label={item.calloutLabel} content={item.calloutContent!} />
      )}

      {/* ── Image (chart/diagram/table) ─────────────────────────────── */}
      {hasImage && (
        <CardImage image={item.image} imageMobile={item.imageMobile} />
      )}

      {/* ── Callout AFTER image (default) ───────────────────────────── */}
      {hasCallout && !item.calloutBeforeImage && (
        <CardCallout label={item.calloutLabel} content={item.calloutContent!} />
      )}

      {/* ── Examples accordion (bordered top, collapsible) ──────────── */}
      {hasExamples && <ExamplesAccordion content={item.examplesContent!} />}
    </div>
  );
}
